import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { ThemeToggle } from "../sirius/ThemeProvider";
import { LanguageToggle } from "../i18n";

export default function Login() {
  const { user, loading, login, verify2FA, resend2FA } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // 2FA state
  const [challenge, setChallenge] = useState(null);  // { challenge_id, email_hint }
  const [code, setCode] = useState("");
  const [resentNote, setResentNote] = useState("");

  // Recovery state
  const [showRecover, setShowRecover] = useState(false);
  const [recoverEmail, setRecoverEmail] = useState("");
  const [recoverSent, setRecoverSent] = useState(false);

  if (loading) {
    return (
      <div className="srs" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }} data-testid="login-loading">
        <div className="srs-mono" style={{ letterSpacing: "0.3em", color: "var(--srs-ink-dim)", fontSize: 12 }}>LASTER…</div>
      </div>
    );
  }

  if (user) {
    const explicit = new URLSearchParams(location.search).get("next");
    const role = user.role;
    const dest = explicit || (role === "subscriber" ? "/account" : "/amex");
    return <Navigate to={dest} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const result = await login(email.trim(), password);
      if (result && result.requires_2fa) {
        setChallenge({ challenge_id: result.challenge_id, email_hint: result.email_hint });
        setCode("");
      }
      // else navigation happens via the `user` redirect at the top
    } catch (err) {
      setError(err.message || "Innlogging mislyktes");
    } finally {
      setSubmitting(false);
    }
  };

  const onVerifyCode = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await verify2FA({ challenge_id: challenge.challenge_id, code: code.trim() });
      // Successful — `user` is now set, the Navigate above will redirect
    } catch (err) {
      setError(err.message || "Kunne ikke verifisere kode");
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async () => {
    setError("");
    setResentNote("");
    try {
      const fresh = await resend2FA(challenge.challenge_id);
      setChallenge({ challenge_id: fresh.challenge_id, email_hint: fresh.email_hint });
      setResentNote("Ny kode sendt — sjekk innboksen.");
      setCode("");
    } catch (err) {
      setError(err.message || "Kunne ikke sende ny kode");
    }
  };

  const onSendRecovery = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/recover`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: recoverEmail.trim() }),
      });
      setRecoverSent(true);
    } catch (err) {
      setError("Kunne ikke sende gjenopprettings-e-post");
    }
  };

  return (
    <div className="srs" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }} data-testid="login-root">
      <nav style={{ position: "sticky", top: 0, zIndex: 30, background: "color-mix(in srgb, var(--srs-bg) 88%, transparent)", backdropFilter: "blur(14px)", borderBottom: "1px solid var(--srs-line)", padding: "16px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "var(--srs-ink)" }} data-testid="login-brand">
          <span className="srs-logo-dot" />
          <span className="srs-display" style={{ fontSize: 24, fontWeight: 500 }}>EuroPilot</span>
        </Link>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <LanguageToggle />
          <ThemeToggle />
        </div>
      </nav>

      <main style={{ flex: 1, display: "grid", placeItems: "center", padding: "60px 28px" }}>
        {showRecover ? (
          // ---- Recovery form ----
          <form onSubmit={onSendRecovery} className="srs-card" style={{ width: "100%", maxWidth: 420, padding: 40, borderRadius: 24 }} data-testid="recover-form">
            <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-ink-dim)", marginBottom: 12 }}>// GJENOPPRETT</div>
            <h1 className="srs-display" style={{ fontSize: 30, fontWeight: 500, letterSpacing: "-0.02em", margin: "0 0 14px 0", lineHeight: 1.1 }}>Glemt 2FA?</h1>
            {recoverSent ? (
              <>
                <p style={{ color: "var(--srs-ink-dim)", lineHeight: 1.55, fontSize: 14 }} data-testid="recover-sent">
                  Hvis kontoen finnes, har vi sendt deg en lenke som logger deg inn og slår av 2FA.
                  Sjekk e-posten din (lenken er gyldig i 1 time).
                </p>
                <button type="button" onClick={() => { setShowRecover(false); setRecoverSent(false); }}
                  className="srs-cta-ghost" style={{ marginTop: 22, width: "100%", justifyContent: "center" }}>
                  Tilbake til innlogging
                </button>
              </>
            ) : (
              <>
                <p style={{ color: "var(--srs-ink-dim)", lineHeight: 1.55, fontSize: 13, marginBottom: 18 }}>
                  Vi sender deg en e-post som logger deg inn og slår av 2FA, så du kan komme tilbake i kontoen.
                </p>
                <label style={labelCss}>E-post</label>
                <input type="email" required value={recoverEmail} onChange={(e) => setRecoverEmail(e.target.value)}
                  placeholder="ola@example.no" style={inputCss} data-testid="recover-email-input" />
                {error && <div style={errorCss} data-testid="recover-error">{error}</div>}
                <button type="submit" disabled={!recoverEmail} className="srs-cta"
                  style={{ marginTop: 22, width: "100%", justifyContent: "center" }} data-testid="recover-submit-btn">
                  Send gjenopprettings-e-post →
                </button>
                <button type="button" onClick={() => setShowRecover(false)}
                  style={{ marginTop: 16, fontSize: 12, color: "var(--srs-ink-faint)", background: "none", border: "none", cursor: "pointer", textAlign: "center", width: "100%" }}>
                  ← Tilbake
                </button>
              </>
            )}
          </form>
        ) : challenge ? (
          // ---- 2FA code entry ----
          <form onSubmit={onVerifyCode} className="srs-card" style={{ width: "100%", maxWidth: 420, padding: 40, borderRadius: 24 }} data-testid="login-2fa-form">
            <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-ink-dim)", marginBottom: 12 }}>// TO-FAKTOR</div>
            <h1 className="srs-display" style={{ fontSize: 30, fontWeight: 500, letterSpacing: "-0.02em", margin: "0 0 14px 0", lineHeight: 1.1 }}>
              Sjekk e-posten din.
            </h1>
            <p style={{ color: "var(--srs-ink-dim)", lineHeight: 1.55, fontSize: 14, marginBottom: 18 }}>
              Vi sendte en 6-sifret kode til <strong style={{ color: "var(--srs-ink)" }} data-testid="login-2fa-hint">{challenge.email_hint}</strong>.
              Koden er gyldig i 10 minutter.
            </p>
            <label style={labelCss}>6-sifret kode</label>
            <input
              type="text"
              required
              autoFocus
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              style={{ ...inputCss, fontFamily: "'Courier New', monospace", fontSize: 22, letterSpacing: "0.4em", textAlign: "center" }}
              data-testid="login-2fa-code-input"
            />
            {error && <div style={errorCss} data-testid="login-2fa-error">{error}</div>}
            {resentNote && (
              <div style={{ marginTop: 10, fontSize: 12, color: "var(--srs-good)" }} data-testid="login-2fa-resent">{resentNote}</div>
            )}
            <button type="submit" disabled={submitting || code.length !== 6} className="srs-cta"
              style={{ marginTop: 22, width: "100%", justifyContent: "center" }} data-testid="login-2fa-submit-btn">
              {submitting ? "Verifiserer…" : "Bekreft kode →"}
            </button>
            <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
              <button type="button" onClick={onResend} style={linkBtn} data-testid="login-2fa-resend-btn">
                Send ny kode
              </button>
              <button type="button" onClick={() => { setShowRecover(true); setRecoverEmail(email); setError(""); }}
                style={linkBtn} data-testid="login-2fa-recover-btn">
                Mistet tilgang?
              </button>
            </div>
            <button type="button" onClick={() => { setChallenge(null); setCode(""); setError(""); }}
              style={{ marginTop: 14, fontSize: 12, color: "var(--srs-ink-faint)", background: "none", border: "none", cursor: "pointer", width: "100%" }}>
              ← Bytt konto
            </button>
          </form>
        ) : (
          // ---- Default email/password form ----
          <form onSubmit={onSubmit} className="srs-card" style={{ width: "100%", maxWidth: 420, padding: 40, borderRadius: 24 }} data-testid="login-form">
            <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-ink-dim)", marginBottom: 12 }}>// LOGG INN</div>
            <h1 className="srs-display" style={{ fontSize: 36, fontWeight: 500, letterSpacing: "-0.02em", margin: "0 0 24px 0", lineHeight: 1.1 }}>
              Velkommen tilbake.
            </h1>

            <label style={labelCss}>E-post</label>
            <input
              type="email"
              required
              autoComplete="email"
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ola@example.no"
              style={inputCss}
            />

            <label style={{ ...labelCss, marginTop: 14 }}>Passord</label>
            <input
              type="password"
              required
              autoComplete="current-password"
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              style={inputCss}
            />

            {error && <div style={errorCss} data-testid="login-error">{error}</div>}

            <button type="submit" disabled={submitting || !email || !password} className="srs-cta"
              style={{ marginTop: 22, width: "100%", justifyContent: "center" }} data-testid="login-submit-btn">
              {submitting ? "Logger inn…" : "Logg inn →"}
            </button>

            <div style={{ marginTop: 14, textAlign: "center" }}>
              <Link to="/forgot-password" data-testid="login-forgot-password-link"
                style={{ fontSize: 12, color: "var(--srs-ink-dim)", textDecoration: "none", borderBottom: "1px dotted var(--srs-ink-faint)" }}>
                Glemt passord?
              </Link>
            </div>

            <div style={{ marginTop: 20, textAlign: "center", fontSize: 12, color: "var(--srs-ink-faint)" }}>
              Ny bruker?{" "}
              <Link to="/signup" style={{ color: "var(--srs-sun)", textDecoration: "none", fontWeight: 600 }} data-testid="login-to-signup">
                Opprett konto →
              </Link>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}

const labelCss = { display: "block", fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.22em", fontWeight: 600, color: "var(--srs-ink-dim)", textTransform: "uppercase", marginBottom: 8 };
const inputCss = { width: "100%", background: "var(--srs-surface-2)", border: "1px solid var(--srs-line)", borderRadius: 10, padding: "12px 14px", fontSize: 14, color: "var(--srs-ink)", fontFamily: "inherit", outline: "none", boxSizing: "border-box" };
const errorCss = { marginTop: 16, padding: "10px 12px", background: "color-mix(in srgb, var(--srs-warn) 12%, transparent)", border: "1px solid var(--srs-warn)", borderRadius: 10, color: "var(--srs-warn)", fontSize: 12, lineHeight: 1.5 };
const linkBtn = { background: "none", border: "none", color: "var(--srs-sun)", cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 };
